import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { useI18n } from "../i18n";
import ClientThumb from "./ClientThumb";
import "./TemplateSelector.css";

function sanitizeEnvUrl(value) {
  if (!value) return "";
  return value.replace(/^["']|["']$/g, "").trim();
}

function resolveApiUrl() {
  const isLocalEnvironment = typeof window !== "undefined" && window.location.hostname === "localhost";
  if (isLocalEnvironment) {
    return sanitizeEnvUrl(process.env.REACT_APP_API_URL_LOCAL || process.env.REACT_APP_API_URL);
  }
  return sanitizeEnvUrl(process.env.REACT_APP_API_URL || process.env.REACT_APP_API_URL_LOCAL);
}

const API_URL = resolveApiUrl();
const LOCAL_STORAGE_KEY = "olt_templates_v2";

function dedup(list) {
  const seen = new Set();
  return list.filter((d) => {
    const key = d.client_id;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

const TemplateSelector = ({ onTemplateSelect, clearSelectionSignal = 0 }) => {
  const { instance, accounts } = useMsal();
  const { t } = useI18n();
  const [templates, setTemplates] = useState([]);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState("");

  const getApiToken = useCallback(async () => {
    const result = await instance.acquireTokenSilent({
      scopes: ["api://c247d402-5b9d-40f2-b7e6-b15a47c56f22/Api.access"],
      account: accounts[0],
    });
    return result.accessToken;
  }, [accounts, instance]);

  const fetchTemplates = useCallback(
    async () => {
      try {
        const token = await instance.acquireTokenSilent({
          account: accounts[0],
          scopes: ["api://c247d402-5b9d-40f2-b7e6-b15a47c56f22/Api.access"],
        });

        const response = await fetch(`${API_URL}/getTemplate`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token.accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ refresh: false }),
        });

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const data = await response.json();
        const unique = dedup(Array.isArray(data) ? data : []);
        setTemplates(unique);
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(unique));
      } catch (error) {
        console.error("Failed to fetch templates:", error);
        const cached = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            setTemplates(dedup(Array.isArray(parsed) ? parsed : []));
          } catch {
            setTemplates([]);
          }
        }
      }
    },
    [instance, accounts],
  );

  const clientOptions = useMemo(() => {
    const names = new Set(templates.map((d) => d.client_name));
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [templates]);

  const templateOptions = useMemo(() => {
    if (!selectedClient) return [];
    return templates
      .filter((d) => d.client_name === selectedClient)
      .sort((a, b) => (a.template_name || "").localeCompare(b.template_name || ""));
  }, [selectedClient, templates]);

  const templateIndex = useMemo(() => {
    const idx = {};
    templates.forEach((d) => { idx[`client:${d.client_id}`] = d; });
    return idx;
  }, [templates]);

  useEffect(() => {
    if (!selectedClient) return;
    if (templateOptions.length === 0) {
      if (selectedTemplateId) setSelectedTemplateId("");
      return;
    }
    const currentExists = templateOptions.some((d) => `client:${d.client_id}` === selectedTemplateId);
    if (!selectedTemplateId || !currentExists) {
      setSelectedTemplateId(`client:${templateOptions[0].client_id}`);
    }
  }, [selectedClient, selectedTemplateId, templateOptions]);

  useEffect(() => {
    const cached = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        setTemplates(dedup(Array.isArray(parsed) ? parsed : []));
      } catch { /* ignore */ }
    }
    fetchTemplates();
  }, [fetchTemplates]);

  const handleAddTemplate = () => {
    if (!selectedTemplateId) return;
    setSelectedTemplates((prev) =>
      prev.includes(selectedTemplateId) ? prev : [...prev, selectedTemplateId],
    );
    setSelectedTemplateId("");
  };

  const handleRemoveTemplate = (tplId) => {
    setSelectedTemplates((prev) => prev.filter((id) => id !== tplId));
  };

  useEffect(() => {
    onTemplateSelect(selectedTemplates);
  }, [selectedTemplates, onTemplateSelect]);

  useEffect(() => {
    setSelectedTemplates([]);
    setSelectedClient("");
    setSelectedTemplateId("");
  }, [clearSelectionSignal]);

  return (
    <div className="template-selector">
      <div className="template-controls">
        <select
          className="template-select"
          value={selectedClient}
          onChange={(e) => setSelectedClient(e.target.value)}
        >
          <option value="">{t("select_client")}</option>
          {clientOptions.map((client) => (
            <option key={client} value={client}>{client}</option>
          ))}
        </select>

        <div className="template-header">
          <select
            className="template-select"
            value={selectedTemplateId}
            onChange={(e) => setSelectedTemplateId(e.target.value)}
            disabled={!selectedClient}
          >
            <option value="">
              {selectedClient ? t("select_pdf") : t("select_client_first")}
            </option>
            {templateOptions.map((d) => (
              <option key={d.client_id} value={`client:${d.client_id}`}>
                {d.template_name || "Reporting"}
              </option>
            ))}
          </select>
          <button
            className="add-template-button"
            onClick={handleAddTemplate}
            disabled={!selectedTemplateId}
            title={t("add_pdf")}
            type="button"
          >
            +
          </button>
        </div>
      </div>

      {selectedTemplates.length > 0 && (
        <div className="template-pending-list">
          <div className="template-pending-title">{t("selected_templates")}</div>
          <ul>
            {selectedTemplates.map((tplId) => {
              const tpl = templateIndex[tplId];
              const clientName = tpl?.client_name || "Unknown";
              const tplName = tpl?.template_name || "Reporting";

              return (
                <li key={tplId} className="template-pending-item">
                  <div className="template-pending-copy">
                    <div className="template-pending-client">
                      <ClientThumb clientName={clientName} getApiToken={getApiToken} size={18} />
                      <strong>{clientName}</strong>
                    </div>
                    <span>{tplName}</span>
                  </div>
                  <button
                    className="remove-template-button"
                    onClick={() => handleRemoveTemplate(tplId)}
                    type="button"
                    title={t("delete")}
                  >
                    ×
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default TemplateSelector;
